"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  year: "2018",
  tag: "FOUNDED",
  title: "Architecting a new capital model.",
  description: "BeneWealth Financial was founded to architect a new model for capital access and investor support.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/founding_blueprint.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/founding_blueprint.png",
  sizes: "400px"
}, {
  year: "2026",
  tag: "ARCHITECTING FUTURE",
  title: "The standard for private capital deployment.",
  description: "Today, BeneWealth Financial stands as the premier architect of capital, funding the next generation of real estate development with precision and speed.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/future_vision.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/future_vision.png",
  sizes: "400px"
}];
function MilestoneRow({
  milestone,
  index
}: {
  milestone: (typeof milestones)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 mb-20 md:mb-28 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="hidden md:block absolute left-1/2 top-8 -translate-x-1/2 z-10">
        <div className="relative flex items-center justify-center">
          <div className="size-4 rounded-lg" style={{
          backgroundColor: "var(--gold-accent)"
        }} />
          <div className="absolute size-8 rounded-lg opacity-30" style={{
          backgroundColor: "var(--gold-accent)"
        }} />
        </div>
      </div>

      <div className={`${isLeft ? "md:pr-16 md:text-right order-1" : "md:pr-16 md:text-right order-1 md:order-1 md:invisible"}`}>
        {isLeft && <div className="relative">
            <div className="font-default text-6xl md:text-7xl lg:text-[5rem] leading-none font-bold tracking-tighter mb-4" style={{
          color: "var(--blueprint-white)",
          fontVariantNumeric: "tabular-nums"
        }}>
              {milestone.year}
            </div>
            <div className="hidden md:block absolute top-10 right-0 h-px" style={{
          backgroundColor: "var(--gold-accent)",
          width: "calc(4rem - 0.5rem)",
          transform: "translateX(100%)"
        }} />
            <MilestoneCard milestone={milestone} align="right" />
          </div>}
      </div>

      <div className={`${!isLeft ? "md:pl-16 order-2" : "md:pl-16 order-2 md:invisible"}`}>
        {!isLeft && <div className="relative">
            <div className="font-default text-6xl md:text-7xl lg:text-[5rem] leading-none font-bold tracking-tighter mb-4" style={{
          color: "var(--blueprint-white)",
          fontVariantNumeric: "tabular-nums"
        }}>
              {milestone.year}
            </div>
            <div className="hidden md:block absolute top-10 left-0 h-px" style={{
          backgroundColor: "var(--gold-accent)",
          width: "calc(4rem - 0.5rem)",
          transform: "translateX(-100%)"
        }} />
            <MilestoneCard milestone={milestone} align="left" />
          </div>}
      </div>
    </div>;
}
function MilestoneCard({
  milestone,
  align
}: {
  milestone: (typeof milestones)[number];
  align: "left" | "right";
}) {
  return <div className={`bg-card border border-border rounded-lg p-8 shadow-lg ${align === "right" ? "md:text-right" : "md:text-left"} text-left`}>
      <div className="font-mono text-xs tracking-[0.2em] uppercase mb-3" style={{
      color: "var(--gold-accent)"
    }}>
        {milestone.tag}
      </div>
      <h3 className="font-default font-semibold text-xl md:text-2xl leading-snug mb-3 text-white">
        {milestone.title}
      </h3>
      <p className="font-default text-sm leading-relaxed text-slate-muted mb-4">
        {milestone.description}
      </p>
      {milestone.image && <div className={`overflow-hidden rounded-lg ${align === "right" ? "md:ml-auto" : ""}`} style={{
      maxWidth: "240px"
    }}>
          <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.title} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
        </div>}
    </div>;
}
export default function Timeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: presentRef,
    isVisible: presentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="64" id="the-benewealth-timeline" className="relative bg-background py-24 md:py-32 overflow-hidden bg-slate-950">
      <div className="container mx-auto px-6 md:px-10 max-w-6xl">
        <div ref={headerRef} className={`max-w-2xl mb-20 md:mb-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-default text-5xl md:text-6xl leading-[1.05] text-white mb-6" style={{
          fontWeight: 700
        }}>
            Architecting
            <br />
            <em className="italic" style={{
            fontWeight: 400
          }}>
              Capital Infrastructure.
            </em>
          </h2>
          <p className="font-default text-base md:text-lg leading-relaxed" style={{
          color: "var(--slate-muted)"
        }}>
            BeneWealth Financial was founded in 2018 and began partnering with private funding investors in 2026 to be able to offer a plethora of real estate funding opportunities to businesses that want to invest in real estate.
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px" style={{
          backgroundColor: "var(--blueprint-white)",
          opacity: 0.2
        }} aria-hidden="true" />
          <div className="md:hidden absolute left-2 top-0 bottom-0 w-px" style={{
          backgroundColor: "var(--blueprint-white)",
          opacity: 0.2
        }} aria-hidden="true" />

          {milestones.map((milestone, i) => <MilestoneRow key={milestone.year} milestone={milestone} index={i} />)}

          <div ref={presentRef} data-index={milestones.length} className={`relative flex flex-col items-center text-center pt-8 transition-all duration-700 ease-out ${presentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 z-10">
              <div className="relative flex items-center justify-center">
                <div className="size-5 rounded-lg" style={{
                backgroundColor: "var(--gold-accent)"
              }} />
                <div className="absolute size-10 rounded-lg opacity-30 animate-pulse" style={{
                backgroundColor: "var(--gold-accent)"
              }} />
              </div>
            </div>
            <div className="mt-12 max-w-xl mx-auto">
              <div className="font-mono text-xs tracking-[0.25em] uppercase mb-4" style={{
              color: "var(--gold-accent)"
            }}>
                Present Day
              </div>
              <div className="font-default text-6xl md:text-7xl leading-none font-bold mb-6" style={{
              color: "var(--blueprint-white)",
              fontVariantNumeric: "tabular-nums"
            }}>
                2026
              </div>
              <h3 className="font-default font-semibold text-2xl md:text-3xl leading-snug text-white mb-4">
                The next phase of growth.
              </h3>
              <p className="font-default text-base leading-relaxed mb-8" style={{
              color: "var(--slate-muted)"
            }}>
                Join the network of investors and partners building the future of real estate. Let's discuss your next project.
              </p>
              <Button asChild size="lg" className="rounded-lg font-default bg-yellow-500 text-slate-950 hover:bg-yellow-400 font-bold">
                <Link to="/PreQualificationFormPage">
                  Get Pre-Qualified
                  <ArrowRight className="ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}